"use client";
import { Label } from "@/components/ui/label";
import { useEffect, useRef } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSettingsSidebar } from "@/hooks/useSettingsSidebar";
import { IBlocks } from "@/types";
import { useDynamicSidebar } from "@/lib/hooks/use-dynamic-sidebar";
import type { BlockUpdateFunction } from "./blocks-map";

interface AdminPopoverProps {
    edit?: boolean;
    children: React.ReactNode;
    className?: string;
    title?: string;
    block: IBlocks;
    update?: BlockUpdateFunction;
    setBlockUpdate: React.Dispatch<React.SetStateAction<IBlocks>>;
}

export default function AdminPopover({
    edit,
    children,
    className,
    title,
    block,
    update,
    setBlockUpdate,
}: AdminPopoverProps) {
    if (!edit) {
        return <>{children}</>;
    }
    const popoverRef = useRef<HTMLDivElement>(null);
    const isMobile = useIsMobile();
    const { isOpen: sidebarIsOpen, toggleSidebar: toggleSettingsSidebar } =
        useDynamicSidebar(["render-settings-sidebar-state"]);
    let {
        setValue: setSidebarView,
        selectedBlock: { setValue: setSelectedBlock, value: selectedBlock },
    } = useSettingsSidebar();
    const isExpanded = selectedBlock?.id == block.id;
    useEffect(() => {
        if (popoverRef.current && isMobile) {
            popoverRef.current.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        } else if (popoverRef.current && isExpanded) {
            const rect = popoverRef.current.getBoundingClientRect();
            // Check if any part of the element is within the viewport
            const isVisible =
                rect.top < window.innerHeight &&
                rect.bottom >= 0 &&
                rect.left < window.innerWidth &&
                rect.right >= 0;
            if (!isVisible) {
                popoverRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            }
        }
    }, [isMobile, isExpanded]);
    const handleExpand = () => {
        if (!sidebarIsOpen) {
            toggleSettingsSidebar();
        }
        setSidebarView("block-details");
        setSelectedBlock(block);
    };
    const handleClose = () => {
        setSidebarView("root");
        setSelectedBlock(null);
    };
    useEffect(() => {
        if (!sidebarIsOpen) {
            handleClose();
        }
    }, [sidebarIsOpen]);
    function handleUpdate(value: IBlocks) {
        if (value == null) {
            update?.(block.id, value, true);
            return;
        }
        setBlockUpdate?.({
            ...block,
            ...value,
        });
        if (block == value) return;
        update?.(block.id, value);
    }
    useEffect(() => {
        const abortController = new AbortController();
        window.addEventListener(
            "updateBlock-" + block.id,
            {
                handleEvent: (event: CustomEvent) => {
                    if (event.detail.block === null) {
                        update?.(block.id, event.detail.block, true);
                        handleClose();
                    } else if (event.detail.block) {
                        handleUpdate(event.detail.block);
                    }
                },
            },
            {
                signal: abortController.signal,
            }
        );
        window.addEventListener(
            "updateBlockInternal-" + block.id,
            {
                handleEvent: (event: CustomEvent) => {
                    if (event.detail.block) {
                        setBlockUpdate?.(event.detail.block);
                    }
                },
            },
            {
                signal: abortController.signal,
            }
        );
        return () => {
            abortController.abort();
        };
    });
    return (
        <div
            ref={popoverRef}
            onClick={() => {
                if (isExpanded) {
                    handleClose();
                } else {
                    handleExpand();
                }
            }}
            className={`group relative z-40 w-full cursor-pointer rounded-md border-2 p-1 transition-all duration-300 hover:border-solid hover:border-primary data-[state=open]:border-solid data-[state=open]:border-primary
            ${isExpanded ? "border border-primary" : "border-transparent"}
            ${className}`}
        >
            <div className="relative flex w-full cursor-pointer flex-col gap-1">
                <div
                    className={`absolute -right-1 -top-1 z-40 flex items-center rounded-bl-md bg-primary p-1 opacity-0 transition-all duration-300 group-hover:opacity-100
                    ${isExpanded && "opacity-100"}`}
                >
                    <Label variant="toggle">{title}</Label>
                </div>
                <div className={`w-full ${edit && ""}`}>
                    <div
                        className={`w-full ${edit && "absolute left-0 top-0 z-40 flex h-full justify-center rounded-md transition-all duration-300 group-hover:bg-primary/30"}`}
                    />
                    {children}
                </div>
            </div>
        </div>
    );
}
